/**
 * Katie & Laura — Wishlist (guest localStorage + live Customer metafield)
 *
 * Wishlist ≠ Favorites. Do not touch Klaviyo `Wants-<Style>` tags.
 *
 * Guest SOT: localStorage key `kl_wishlist_guest`
 *
 * Logged-in SOT (LIVE Customer metafields):
 *   diary.wishlist       json array of { productId, variantId, handle, title, image, addedAt, note? }
 *   diary.wishlist_share json — discreet share; theme never sends `note` (includeNotes: false)
 *
 * Theme cannot write Customer metafields. Optimistic writes stay in `kl_wishlist_guest`.
 * Logged-in SOT is the account list via App Proxy (HMAC + logged_in_customer_id).
 * Hydrate always GET /wishlist when signed in — do not gate on empty localStorage.
 * Leftover guest hearts merge once (POST /wishlist/merge), then account wins.
 * On hydrate, keep prior image/title/handle when proxy omits them (CD read has no image).
 * Guest stays localStorage-only. Never put WISHLIST_PROXY_SECRET in theme JS.
 *
 * Events (detail = item payload, except toggle which is { item, added }):
 *   wishlist:add | wishlist:remove | wishlist:toggle | wishlist:note
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'kl_wishlist_guest';
  var MERGE_FLAG = 'kl_wishlist_merged';
  var SELECTOR = '[data-kl-wishlist-toggle]';
  var DEFAULT_PROXY_PATH = '/apps/kl-wishlist';
  var NOTE_MAX = 500;
  var cache = null;
  var mergeStarted = false;

  function config() {
    return global.KLWishlistConfig || {};
  }

  function strings() {
    var s = config().strings || {};
    return {
      add: s.add || 'Save to wishlist',
      remove: s.remove || 'Remove from wishlist',
      added: s.added || 'Saved to wishlist',
      removed: s.removed || 'Removed from wishlist',
      removeConfirm: s.removeConfirm || 'Remove from Wishlist?'
    };
  }

  function normalizeId(id) {
    if (id == null || id === '') return '';
    var match = String(id).match(/(\d+)\s*$/);
    return match ? match[1] : String(id);
  }

  function normalizeNote(value) {
    if (value == null) return '';
    var note = String(value).replace(/^\s+|\s+$/g, '');
    if (note.length > NOTE_MAX) note = note.slice(0, NOTE_MAX);
    return note;
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var productId = normalizeId(raw.productId);
    if (!productId) return null;
    var item = {
      productId: productId,
      variantId: normalizeId(raw.variantId),
      handle: raw.handle ? String(raw.handle) : '',
      title: raw.title ? String(raw.title) : '',
      image: raw.image ? String(raw.image) : '',
      addedAt: raw.addedAt ? String(raw.addedAt) : new Date().toISOString()
    };
    var note = normalizeNote(raw.note);
    if (note) item.note = note;
    return item;
  }

  function normalizeList(value) {
    if (!value) return [];
    var list = value;
    if (typeof list === 'string') {
      try {
        list = JSON.parse(list);
      } catch (e) {
        return [];
      }
    }
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = normalizeItem(list[i]);
      if (!item || seen[item.productId]) continue;
      seen[item.productId] = true;
      out.push(item);
    }
    return out;
  }

  function readGuest() {
    try {
      return normalizeList(global.localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return [];
    }
  }

  function writeGuest(items) {
    cache = items;
    try {
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode / quota — UI still updates for this page */
    }
    return items;
  }

  function load() {
    if (cache) return cache;
    var guest = readGuest();
    var cfg = config();
    var server = normalizeList(cfg.customerWishlist);

    // Logged-in: account metafield is the seed, even when localStorage is stale.
    // Unmerged leftover guest hearts stay until mergeOnLogin + proxy refresh.
    // `diary.wishlist_share` is not read here.
    if (cfg.loggedIn) {
      if (guest.length && !readMergeFlag()) {
        cache = guest;
        return cache;
      }
      if (server.length) {
        cache = writeGuest(server);
        return cache;
      }
    }

    cache = guest;
    return cache;
  }

  function announce(message) {
    var el = document.getElementById('status-announcement');
    if (el) el.textContent = message;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /**
   * Logged-in App Proxy contract (Shopify forwards HMAC + logged_in_customer_id).
   *
   * Prefer `KLWishlistConfig.appProxyPath` (default `/apps/kl-wishlist`) for
   * browser calls. `workerUrl` is docs/future only — do not fetch it from
   * theme JS (no WISHLIST_PROXY_SECRET, no Authorization, no Admin tokens).
   *
 *   GET  {proxyPath}/wishlist        live list (logged-in hydrate, any local cache)
 *   POST {proxyPath}/wishlist/add    body = item
 *   POST {proxyPath}/wishlist/remove body = { variantId } or { productId }
 *   POST {proxyPath}/wishlist/merge  body = guest array (init, once)
 *   POST {proxyPath}/wishlist/note   body = { variantId|productId, note } (soft-fail 404)
 *   GET  {proxyPath}/share/:token    public list JSON (theme page renders it)
 *   POST {proxyPath}/wishlist/share  body = { includeTitles, includeNotes: false }
 * Notes & photos personal uploads (source: upload) are never included.
 *     → rewrite workers.dev /share/:token to {origin}/pages/wish-list?share=:token
 *
 * Worker persists Customer metafield diary.wishlist. Share POST writes
 * diary.wishlist_share. Theme never invents tokens or stores secrets.
 * Leave Klaviyo Wants-<Style> alone.
   *
   * Soft-fail: guest `kl_wishlist_guest` is already written before this runs.
   */
  function appProxyPath() {
    var raw = String(config().appProxyPath || '').trim();
    if (!raw) raw = DEFAULT_PROXY_PATH;
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function canSync() {
    var cfg = config();
    return !!(cfg.loggedIn && cfg.syncEnabled && appProxyPath());
  }

  function mergeFlagKey() {
    var id = normalizeId(config().customerId);
    return id ? MERGE_FLAG + ':' + id : MERGE_FLAG;
  }

  function readMergeFlag() {
    try {
      return global.sessionStorage.getItem(mergeFlagKey()) === '1';
    } catch (e) {
      return false;
    }
  }

  function markMerged() {
    try {
      global.sessionStorage.setItem(mergeFlagKey(), '1');
    } catch (e) {}
  }

  function itemsFromResponse(data) {
    if (!data) return null;
    if (Array.isArray(data)) return normalizeList(data);
    if (Array.isArray(data.items)) return normalizeList(data.items);
    if (Array.isArray(data.wishlist)) return normalizeList(data.wishlist);
    return null;
  }

  function localNoteMap(items) {
    var map = {};
    if (!items) return map;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || !it.note) continue;
      if (it.productId) map['p:' + it.productId] = it.note;
      if (it.variantId) map['v:' + it.variantId] = it.note;
    }
    return map;
  }

  function applyLocalNotes(incoming, previous) {
    if (!incoming || !incoming.length) return incoming;
    var kept = localNoteMap(previous);
    var out = [];
    for (var i = 0; i < incoming.length; i++) {
      var item = Object.assign({}, incoming[i]);
      if (!item.note) {
        var found = kept['p:' + item.productId] || (item.variantId ? kept['v:' + item.variantId] : '');
        if (found) item.note = found;
      }
      out.push(item);
    }
    return out;
  }

  /** Keep good thumbs/titles/handles when App Proxy CD read omits image. */
  function localDisplayMap(items) {
    var map = {};
    if (!items) return map;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || !it.productId) continue;
      map['p:' + it.productId] = it;
      if (it.variantId) map['v:' + it.variantId] = it;
    }
    return map;
  }

  function applyLocalDisplay(incoming, previous) {
    if (!incoming || !incoming.length) return incoming;
    var kept = localDisplayMap(previous);
    var out = [];
    for (var i = 0; i < incoming.length; i++) {
      var item = Object.assign({}, incoming[i]);
      var prev = kept['p:' + item.productId] || (item.variantId ? kept['v:' + item.variantId] : null);
      if (prev) {
        if (!item.image && prev.image) item.image = prev.image;
        if (!item.title && prev.title) item.title = prev.title;
        if (!item.handle && prev.handle) item.handle = prev.handle;
      }
      out.push(item);
    }
    return out;
  }

  function applyLocalFields(incoming, previous) {
    return applyLocalDisplay(applyLocalNotes(incoming, previous), previous);
  }

  function indexById(items, rawId) {
    var id = normalizeId(rawId);
    if (!id || !items) return -1;
    var i;
    for (i = 0; i < items.length; i++) {
      if (items[i].productId === id) return i;
    }
    for (i = 0; i < items.length; i++) {
      if (items[i].variantId === id) return i;
    }
    return -1;
  }

  function proxyGet(route) {
    return fetch(appProxyPath() + route, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('kl-wishlist ' + res.status);
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function proxyPost(route, body) {
    return fetch(appProxyPath() + route, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('kl-wishlist ' + res.status);
      return res.json().catch(function () {
        return {};
      });
    });
  }

  /**
   * Live read from App Proxy. Logged-in callers always hydrate — do not wait
   * for an empty guest list. mergeOnLogin still owns leftover-heart merge.
   * Soft-fail: keep the current local list. Preserve local image/title/handle.
   */
  function refreshFromProxy() {
    var cfg = config();
    if (!cfg.loggedIn || !appProxyPath()) return Promise.resolve(list());
    return proxyGet('/wishlist')
      .then(function (data) {
        var next = itemsFromResponse(data);
        if (next) {
          next = applyLocalFields(next, load());
          writeGuest(next);
          paintAll(document);
          emit('wishlist:sync', { items: next });
        }
        return list();
      })
      .catch(function () {
        return list();
      });
  }

  function syncLoggedIn(action, item) {
    if (!canSync()) return;
    var req = null;
    if (action === 'add' && item) {
      req = proxyPost('/wishlist/add', item);
    } else if (action === 'remove' && item) {
      var body = item.variantId
        ? { variantId: item.variantId }
        : { productId: item.productId };
      req = proxyPost('/wishlist/remove', body);
    }
    if (!req) return;
    req.catch(function () {
      /* guest list already written */
    });
  }

  function shopShareOrigin() {
    return typeof location !== 'undefined' && location.origin ? location.origin : '';
  }

  function shareTokenFromPayload(data) {
    if (global.KLWishPage && typeof global.KLWishPage.tokenFromPayload === 'function') {
      return global.KLWishPage.tokenFromPayload(data);
    }
    if (!data || typeof data !== 'object') return '';
    if (data.token) return String(data.token).trim();
    var src = String(data.url || data.path || '');
    var query = src.match(/[?&#]share=([^&]+)/);
    if (query && query[1]) {
      try {
        return decodeURIComponent(query[1]).trim();
      } catch (e) {
        return query[1];
      }
    }
    var path = src.match(/\/share\/([^/?#]+)/);
    return path && path[1] ? path[1] : '';
  }

  function shopWishShareUrl(token) {
    var t = String(token || '').trim();
    if (!t) return '';
    return shopShareOrigin() + '/pages/wish-list?share=' + encodeURIComponent(t);
  }

  function mintedShareUrl(data) {
    if (global.KLWishPage && typeof global.KLWishPage.resolveShareUrl === 'function') {
      return global.KLWishPage.resolveShareUrl(data || {}, shopShareOrigin());
    }
    var token = shareTokenFromPayload(data);
    var url = data && data.url != null ? String(data.url).trim() : '';
    var path = data && data.path != null ? String(data.path).trim() : '';
    var candidate = url || path;
    var onShop = false;
    try {
      onShop = !!(candidate && new URL(candidate, shopShareOrigin()).host === new URL(shopShareOrigin()).host);
    } catch (e) {
      onShop = false;
    }
    if (candidate && /\/pages\/wish-list\?share=/i.test(candidate) && !/workers\.dev/i.test(candidate)) {
      if (/^(https?:)?\/\//i.test(candidate)) return candidate;
      return shopShareOrigin() + (candidate.charAt(0) === '/' ? candidate : '/' + candidate);
    }
    if (onShop && !/\/share\/[^/?#]+/i.test(candidate) && !/workers\.dev/i.test(candidate)) {
      return /^(https?:)?\/\//i.test(candidate) ? candidate : shopShareOrigin() + (candidate.charAt(0) === '/' ? candidate : '/' + candidate);
    }
    return shopWishShareUrl(token);
  }

  function createShare(opts) {
    if (!config().loggedIn) {
      var signIn = new Error('sign-in');
      signIn.code = 'sign-in';
      return Promise.reject(signIn);
    }
    return proxyPost('/wishlist/share', {
      includeTitles: !!(opts && opts.includeTitles),
      includeNotes: false
    }).then(function (data) {
      var next = Object.assign({}, data || {});
      var token = shareTokenFromPayload(next);
      var link = mintedShareUrl(next);
      if (token) next.token = token;
      if (link) {
        next.url = link;
        try {
          var parsed = new URL(link, shopShareOrigin());
          next.path = parsed.pathname + parsed.search;
        } catch (e) {
          next.path = '/pages/wish-list?share=' + encodeURIComponent(token);
        }
      }
      return next;
    }).catch(function (err) {
      if (err && err.message) {
        var match = String(err.message).match(/(\d+)\s*$/);
        if (match) err.status = Number(match[1]);
      }
      throw err;
    });
  }

  function mergeOnLogin(guestItems) {
    if (!canSync() || readMergeFlag() || mergeStarted) return Promise.resolve(false);
    var guest = normalizeList(guestItems);
    if (!guest.length) return Promise.resolve(false);
    mergeStarted = true;
    return proxyPost('/wishlist/merge', guest)
      .then(function (data) {
        var next = itemsFromResponse(data);
        if (next) {
          next = applyLocalFields(next, load());
          writeGuest(next);
          paintAll(document);
          emit('wishlist:sync', { items: next });
        }
        markMerged();
        return true;
      })
      .catch(function () {
        mergeStarted = false;
        /* keep guest list; retry on next page load */
        return false;
      });
  }

  function hydrateLoggedIn(guestBefore) {
    if (!config().loggedIn) return;
    mergeOnLogin(guestBefore).then(function () {
      return refreshFromProxy();
    });
  }

  function paintButton(btn) {
    var item = itemFromButton(btn);
    if (!item) return;
    var on = has(item.productId);
    var copy = strings();
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? copy.remove : copy.add);
  }

  function paintAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < buttons.length; i++) paintButton(buttons[i]);
    if (root && root.matches && root.matches(SELECTOR)) paintButton(root);
  }

  function itemFromButton(btn) {
    if (!btn) return null;
    var productId = btn.getAttribute('data-product-id');
    var variantId = btn.getAttribute('data-variant-id');
    var host = btn.closest('product-rerender, .product, product-card, .horizontal-product-card');
    var form = host && host.querySelector && host.querySelector('form[action*="/cart/add"]');
    var idInput = form && form.querySelector('[name="id"]:not([disabled])');
    if (idInput && idInput.value) variantId = idInput.value;

    return normalizeItem({
      productId: productId,
      variantId: variantId,
      handle: btn.getAttribute('data-handle'),
      title: btn.getAttribute('data-title'),
      image: btn.getAttribute('data-image')
    });
  }

  function has(productId) {
    var id = normalizeId(productId);
    if (!id) return false;
    var items = load();
    for (var i = 0; i < items.length; i++) {
      if (items[i].productId === id) return true;
    }
    return false;
  }

  function list() {
    return load().slice();
  }

  function noteBody(item, note) {
    var body = { note: note };
    if (item.productId) body.productId = item.productId;
    if (item.variantId) body.variantId = item.variantId;
    return body;
  }

  function syncNoteLoggedIn(item, note) {
    var cfg = config();
    if (!cfg.loggedIn || !appProxyPath()) return;
    proxyPost('/wishlist/note', noteBody(item, note)).catch(function () {
      /* 404 until Worker ships — local note already written */
    });
  }

  /**
   * Personal note on a saved item. Guest: localStorage only.
   * Logged-in: also POST /wishlist/note (soft-fail). Never sent on public share.
   */
  function setNote(idOrItem, note) {
    var id = idOrItem;
    if (idOrItem && typeof idOrItem === 'object') {
      id = idOrItem.variantId || idOrItem.productId;
    }
    var items = load().slice();
    var idx = indexById(items, id);
    if (idx < 0) return false;
    var nextItem = Object.assign({}, items[idx]);
    var noteStr = normalizeNote(note);
    if (noteStr) nextItem.note = noteStr;
    else delete nextItem.note;
    items[idx] = nextItem;
    writeGuest(items);
    syncNoteLoggedIn(nextItem, noteStr);
    emit('wishlist:note', nextItem);
    return nextItem;
  }

  function persist(next, action, item) {
    writeGuest(next);
    var copy = strings();
    if (action === 'add') {
      emit('wishlist:add', item);
      announce(copy.added);
    } else if (action === 'remove') {
      emit('wishlist:remove', item);
      announce(copy.removed);
    }
    emit('wishlist:toggle', { item: item, added: action === 'add' });
    paintAll(document);
    syncLoggedIn(action, item);
    return action === 'add';
  }

  function add(raw) {
    var item = normalizeItem(raw);
    if (!item) return false;
    if (has(item.productId)) return true;
    item.addedAt = item.addedAt || new Date().toISOString();
    return persist(load().concat([item]), 'add', item);
  }

  function remove(raw) {
    var item = normalizeItem(typeof raw === 'object' ? raw : { productId: raw });
    if (!item) return false;
    var id = item.productId;
    var existing = null;
    var next = [];
    var items = load();
    for (var i = 0; i < items.length; i++) {
      if (items[i].productId === id) existing = items[i];
      else next.push(items[i]);
    }
    if (!existing) return false;
    var payload = Object.assign({}, existing, item);
    return persist(next, 'remove', payload);
  }

  function toggle(raw) {
    var item = normalizeItem(raw);
    if (!item) return false;
    if (has(item.productId)) return remove(item);
    return add(item);
  }

  function onClick(event) {
    var btn = event.target && event.target.closest && event.target.closest(SELECTOR);
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    var item = itemFromButton(btn);
    if (!item) return;
    if (btn.closest('[data-kl-wishlist-page]') && has(item.productId)) {
      askWishPageRemoveConfirm(btn, function (ok) {
        if (ok) remove(item);
      });
      return;
    }
    toggle(item);
  }

  function askWishPageRemoveConfirm(btn, cb) {
    var ask = global.KLWishPage && global.KLWishPage.confirmRemove;
    if (typeof ask === 'function') {
      ask(cb);
      return;
    }
    var page = btn && btn.closest && btn.closest('[data-kl-wishlist-page]');
    var message =
      (page && page.getAttribute('data-remove-confirm')) ||
      strings().removeConfirm ||
      'Remove from Wishlist?';
    cb(typeof global.confirm === 'function' ? !!global.confirm(message) : false);
  }

  function init() {
    var guestBefore = readGuest();
    load();
    paintAll(document);
    document.addEventListener('click', onClick, true);
    hydrateLoggedIn(guestBefore);

    if (typeof MutationObserver !== 'undefined') {
      var timer = null;
      var observer = new MutationObserver(function (mutations) {
        var needsPaint = false;
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(SELECTOR) || (node.querySelector && node.querySelector(SELECTOR))) {
              needsPaint = true;
              break;
            }
          }
          if (needsPaint) break;
        }
        if (!needsPaint) return;
        clearTimeout(timer);
        timer = setTimeout(function () {
          paintAll(document);
        }, 50);
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  var api = {
    storageKey: STORAGE_KEY,
    metafield: { namespace: 'diary', key: 'wishlist' },
    shareMetafield: { namespace: 'diary', key: 'wishlist_share', ignored: true },
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    list: list,
    setNote: setNote,
    refreshFromProxy: refreshFromProxy,
    createShare: createShare,
    resolveShareUrl: mintedShareUrl,
    syncLoggedIn: syncLoggedIn,
    mergeOnLogin: mergeOnLogin,
    syncButtons: paintAll
  };

  global.KLWishlist = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(typeof window !== 'undefined' ? window : globalThis);
